(() => {
function defineCustomElement(name, constructor, options) {
  if (!customElements.get(name)) customElements.define(name, constructor, options);
}

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.placeholderIndex = 0;
    this.placeholderColorIndex = 0;
    this.requestSerial = 0;
    this.states = { OPEN: 'predictive-search-open', LOADING: 'btn--loading', SEARCH_OPEN: 'search-open' };
  }

  connectedCallback() {
    if (this.isConnectedToSearch) return;
    this.input = this.querySelector('input[type="search"]');
    if (!this.input) return;
    this.isConnectedToSearch = true;
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    this.form = this.querySelector('form[role="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    this.searchContent = this.querySelector('.search__content');
    this.resultsElement = this.querySelector('[data-predictive-results]');
    this.statusElement = this.querySelector('[data-predictive-search-status]');
    this.errorElement = this.querySelector('[data-predictive-search-error]');
    this.loadingElement = this.querySelector('[data-search-loading]');
    this.animatedPlaceholder = this.querySelector('[data-search-placeholder]');
    this.animatedPlaceholderWord = this.querySelector('[data-search-placeholder-word]');
    this.placeholderWords = String(this.dataset.searchPlaceholderWords || '').split('|').filter(Boolean);
    this.placeholderColors = String(this.dataset.searchPlaceholderColors || '').split('|').filter(Boolean);
    this.predictiveEnabled = this.dataset.predictiveEnabled !== 'false';
    this.announcements = {
      resultsEmpty: this.dataset.resultsEmptyAnnouncement || '',
      resultsAvailable: this.dataset.resultsAvailableAnnouncement || '',
      unavailable: this.dataset.unavailableAnnouncement || '',
      loading: this.dataset.loadingAnnouncement || '',
      suggestionsAvailable: this.dataset.suggestionsAvailableAnnouncement || '',
    };
    this.header = this.closest('header');
    this.panel = this.closest('.header__search');
    this.headerSection = this.closest('.header-section');
    this.overlay = this.headerSection?.querySelector('.fixed-overlay--header-section');
    this.closeButton = this.panel?.querySelector('[data-header-search-close]');
    this.isHeaderPanelSearch = Boolean(this.header && this.panel);
    this.isHeaderSearch = Boolean(this.header);
    this.triggers = this.panel?.id ? Array.from(document.querySelectorAll(`[aria-controls="${this.panel.id}"]`)) : [];
    this.historyKey = `tm-product-search:${document.documentElement.lang || 'fr'}`;

    this.input.addEventListener('input', () => {
      this.submitSerial = (this.submitSerial || 0) + 1;
      this.cancelRequest();
      this.resolvedQuery = null;
      this.removeAttribute('results');
      this.clearError();
      this.updateResetState();
      if (!this.predictiveEnabled) return;
      this.toggleSearchState(true);
      this.input.setAttribute('aria-expanded', 'true');
      this.searchContent?.classList.remove('hidden');
      if (!this.getQuery()) { this.showInitialContent(); return; }
      this.setLoadingState(true);
      this.inputTimer = window.setTimeout(() => this.renderSection(this.getQuery()), 180);
    }, { signal });
    this.input.addEventListener('focus', () => {
      this.syncAnimatedPlaceholder();
      if (!this.isHeaderPanelSearch && this.predictiveEnabled) this.showInitialContent();
    }, { signal });
    this.input.addEventListener('blur', () => this.syncAnimatedPlaceholder(), { signal });
    this.form?.addEventListener('submit', event => this.handleSubmit(event), { signal });
    this.resetButton?.addEventListener('click', event => this.clear(event), { signal });
    this.closeButton?.addEventListener('click', () => this.close(), { signal });
    this.overlay?.addEventListener('click', () => this.dismiss(), { signal });
    this.addEventListener('click', event => this.handleSearchLinkClick(event), { signal });
    this.addEventListener('focusout', () => {
      window.setTimeout(() => {
        if (this.isConnected && this.isOpen && !this.isHeaderPanelSearch && !this.contains(document.activeElement)) {
          if (!this.getClientRects().length) this.handleViewportResize();
          else this.dismiss();
        }
      }, 0);
    }, { signal });
    this.triggers.forEach(trigger => trigger.addEventListener('click', event => {
      event.preventDefault();
      if (this.isOpen) this.close(); else this.open(event.currentTarget);
    }, { signal }));
    document.addEventListener('keydown', event => this.handleKeydown(event), { signal });
    document.addEventListener('click', event => {
      if (this.isOpen && !this.isHeaderPanelSearch && !this.contains(event.target)) this.dismiss();
    }, { signal });
    document.addEventListener('menu-drawer:open', () => this.dismiss(), { signal });
    window.addEventListener('resize', () => this.handleViewportResize(), { signal, passive: true });
    const updateViewport = () => { if (this.isOpen) this.setViewportMetrics(); };
    window.addEventListener('scroll', updateViewport, { signal, passive: true });
    window.visualViewport?.addEventListener('resize', updateViewport, { signal, passive: true });
    window.visualViewport?.addEventListener('scroll', updateViewport, { signal, passive: true });
    window.addEventListener('tm:search-history', () => this.renderRecentSearches(), { signal });
    this.querySelector('[data-search-clear-history]')?.addEventListener('click', () => {
      try { sessionStorage.removeItem(this.historyKey); } catch { /* Storage is optional. */ }
      window.dispatchEvent(new Event('tm:search-history'));
      this.input.focus();
    }, { signal });
    this.updateResetState();
    this.initializeAnimatedPlaceholder();
    this.renderRecentSearches();
  }

  disconnectedCallback() {
    this.dismiss();
    this.abortController?.abort();
    this.cancelRequest();
    window.clearTimeout(this.placeholderTimer);
    window.clearTimeout(this.placeholderSwapTimer);
    this.placeholderObserver?.disconnect();
    this.placeholderReady = false;
    this.isConnectedToSearch = false;
  }

  get isOpen() { return this.classList.contains(this.states.OPEN); }
  getQuery() { return this.input.value.trim(); }

  async open(activeElement = null) {
    if (!this.isHeaderPanelSearch || this.isOpen) return;
    const drawer = this.header.querySelector('menu-drawer[open]');
    if (drawer && typeof drawer.hide === 'function') {
      await Promise.race([Promise.resolve(drawer.hide()), new Promise(resolve => window.setTimeout(resolve, 850))]).catch(() => {});
    }
    this.header.querySelectorAll('details[open]').forEach(details => { details.open = false; });
    this.activeElement = activeElement || document.activeElement;
    this.panel.hidden = false;
    this.panel.setAttribute('aria-hidden', 'false');
    this.triggers.forEach(trigger => trigger.setAttribute('aria-expanded', 'true'));
    this.toggleSearchState(true);
    this.setViewportMetrics();
    if (this.predictiveEnabled) this.showInitialContent();
    await new Promise(resolve => window.requestAnimationFrame(resolve));
    if (this.isOpen) FoxTheme.a11y.trapFocus(this.panel, this.input);
  }

  close({ restoreFocus = true } = {}) {
    if (!this.isHeaderPanelSearch || !this.isOpen) return;
    const target = restoreFocus && this.activeElement?.isConnected ? this.activeElement : null;
    this.toggleSearchState(false);
    this.panel.setAttribute('aria-hidden', 'true');
    this.panel.hidden = true;
    this.triggers.forEach(trigger => trigger.setAttribute('aria-expanded', 'false'));
    FoxTheme.a11y.removeTrapFocus(target);
    this.activeElement = null;
  }

  dismiss({ restoreFocus = false } = {}) {
    if (this.isHeaderPanelSearch) this.close({ restoreFocus });
    else this.toggleSearchState(false);
  }

  handleKeydown(event) {
    const insideSearch = this.contains(event.target);
    const insidePanel = this.isHeaderPanelSearch && this.panel.contains(event.target);
    if (!this.isOpen || (!insideSearch && !(event.key === 'Escape' && insidePanel))) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      if (this.isHeaderPanelSearch) this.close();
      else { this.input.focus(); this.toggleSearchState(false); }
      return;
    }
    if (!['ArrowDown', 'ArrowUp'].includes(event.key) || !this.predictiveEnabled) return;
    const links = Array.from(this.searchContent.querySelectorAll('a[href], button:not([disabled])'))
      .filter(element => element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
    if (!links.length) return;
    const current = links.indexOf(document.activeElement);
    if (document.activeElement !== this.input && current === -1) return;
    event.preventDefault();
    const next = current + (event.key === 'ArrowDown' ? 1 : -1);
    if (current === -1) (event.key === 'ArrowDown' ? links[0] : links.at(-1)).focus();
    else if (next < 0 || next >= links.length) this.input.focus();
    else links[next].focus();
  }

  showInitialContent() {
    if (!this.predictiveEnabled) return;
    this.setViewportMetrics();
    this.clearError();
    this.toggleSearchState(true);
    this.searchContent?.classList.remove('hidden');
    this.input.setAttribute('aria-expanded', 'true');
    if (!this.getQuery()) {
      this.removeAttribute('results');
      this.renderRecentSearches();
      this.announce(this.announcements.suggestionsAvailable);
    } else this.renderSection(this.getQuery());
  }

  clear(event) {
    event?.preventDefault();
    this.submitSerial = (this.submitSerial || 0) + 1;
    this.cancelRequest();
    this.resolvedQuery = null;
    this.input.value = '';
    this.removeAttribute('results');
    this.resultsElement?.replaceChildren();
    this.updateResetState();
    this.showInitialContent();
    this.input.focus();
  }

  async handleSubmit(event) {
    if (this.submittingNative) return;
    const original = this.getQuery();
    if (!original) {
      event.preventDefault();
      this.showInitialContent();
      this.input.focus();
      return;
    }
    this.saveRecentSearch(original);
    if (!this.predictiveEnabled || event.submitter?.hasAttribute('data-search-direct-submit')) return;
    event.preventDefault();
    if (this.awaitingSubmit) return;
    this.awaitingSubmit = true;
    const submission = this.submitSerial || 0;
    try {
      // Resolve the same query used by the suggestions, including an early Enter.
      if (this.resolvedQuery?.original !== original) {
        const request = this.pendingQuery === original ? this.pendingRequest : this.renderSection(original);
        let timer;
        await Promise.race([request, new Promise(resolve => { timer = window.setTimeout(resolve, 1400); })]);
        window.clearTimeout(timer);
      }
      // A new task avoids a nested submit when a cached match resolved immediately.
      await new Promise(resolve => window.setTimeout(resolve, 0));
      if (this.getQuery() !== original || !this.isConnected || submission !== (this.submitSerial || 0)) return;
      if (this.resolvedQuery?.original === original) this.input.value = this.resolvedQuery.term;
      this.cancelRequest();
      this.submittingNative = true;
      this.form.requestSubmit();
      this.submittingNative = false;
    } finally { this.awaitingSubmit = false; }
  }

  handleSearchLinkClick(event) {
    const suggestion = event.target.closest?.('[data-search-suggestion]');
    if (suggestion && this.contains(suggestion)) {
      event.preventDefault();
      this.cancelRequest();
      this.resolvedQuery = null;
      this.input.value = suggestion.dataset.searchSuggestion;
      this.updateResetState();
      this.input.focus();
      this.showInitialContent();
    } else if (event.target.closest?.('[data-search-result-link], [data-search-view-all]')) this.saveRecentSearch(this.getQuery());
  }

  readRecentSearches() {
    try {
      const values = JSON.parse(sessionStorage.getItem(this.historyKey) || '[]');
      return Array.isArray(values) ? values.filter(value => typeof value === 'string' && value.length <= 120).slice(0, 5) : [];
    } catch { return []; }
  }

  saveRecentSearch(query) {
    if (!query || query.length > 120) return;
    const values = [query, ...this.readRecentSearches().filter(value => value.toLocaleLowerCase() !== query.toLocaleLowerCase())].slice(0, 5);
    try { sessionStorage.setItem(this.historyKey, JSON.stringify(values)); } catch { /* Storage is optional. */ }
    window.dispatchEvent(new Event('tm:search-history'));
  }

  renderRecentSearches() {
    const container = this.querySelector('[data-search-recent]');
    const list = this.querySelector('[data-search-recent-list]');
    if (!container || !list) return;
    const values = this.readRecentSearches();
    list.replaceChildren(...values.map(query => {
      const item = document.createElement('li');
      const link = document.createElement('a');
      const url = new URL(this.form.action, window.location.origin);
      url.search = new URLSearchParams({ q: query, type: 'product', 'options[prefix]': 'last' });
      link.href = url;
      link.dataset.searchSuggestion = query;
      link.textContent = query;
      item.append(link);
      return item;
    }));
    container.hidden = !values.length;
  }

  setupURL(query) {
    const url = new URL(FoxTheme.routes.predictive_search_url, window.location.origin);
    url.search = new URLSearchParams({ q: query, 'resources[type]': 'product,collection,query',
      'resources[limit]': this.dataset.resultsLimit || '8', 'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,product_type,variants.title,variants.sku,vendor',
      section_id: this.dataset.resultsSection || 'predictive-search' });
    return url;
  }

  getSearchCandidates(query) { return window.TodayMerchSearchQuery?.buildSearchCandidates(query) || [query]; }

  cancelRequest() {
    window.clearTimeout(this.inputTimer);
    this.requestController?.abort();
    this.requestSerial += 1;
    this.pendingQuery = null;
    this.pendingRequest = null;
    this.setLoadingState(false);
  }

  async fetchSearchResults(url, signal) {
    if (this.cachedMap.has(url)) return this.cachedMap.get(url);
    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error(`Predictive search returned ${response.status}`);
    const html = await response.text();
    if (!signal.aborted) {
      if (this.cachedMap.size >= 30) this.cachedMap.delete(this.cachedMap.keys().next().value);
      this.cachedMap.set(url, html);
    }
    return html;
  }

  parseSearchResults(html) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector('[data-predictive-results]');
  }

  renderSection(query) {
    const original = String(query || '').trim();
    if (!original || !this.predictiveEnabled) return Promise.resolve();
    if (this.pendingQuery === original && this.pendingRequest) return this.pendingRequest;
    this.cancelRequest();
    this.requestController = new AbortController();
    const controller = this.requestController;
    const serial = this.requestSerial;
    this.pendingQuery = original;
    this.clearError();
    this.setLoadingState(true);
    const isCurrent = () => !controller.signal.aborted && serial === this.requestSerial && this.getQuery() === original;
    this.pendingRequest = (async () => {
      let timedOut = false;
      const timer = window.setTimeout(() => { timedOut = true; controller.abort(); }, 5000);
      try {
        let selected; let collectionFallback; let selectedTerm = original;
        for (const candidate of this.getSearchCandidates(original)) {
          const html = await this.fetchSearchResults(this.setupURL(candidate).toString(), controller.signal);
          if (!isCurrent()) return;
          const incoming = this.parseSearchResults(html);
          if (!incoming) throw new Error('Predictive search response is missing results');
          selected = incoming;
          selectedTerm = candidate;
          if (Number(incoming.dataset.predictiveProductCount) > 0) break;
          if (!collectionFallback && Number(incoming.dataset.predictiveCollectionCount) > 0) collectionFallback = incoming;
        }
        if (!isCurrent()) return;
        const hasProducts = Number(selected?.dataset.predictiveProductCount) > 0;
        this.resolvedQuery = { original, term: hasProducts ? selectedTerm : original };
        if (this.isOpen) this.renderSearchResults(hasProducts ? selected : collectionFallback || selected, original);
      } catch (error) {
        if (serial !== this.requestSerial || this.getQuery() !== original) return;
        if ((error.name !== 'AbortError' || timedOut) && this.isOpen) this.showError();
      } finally {
        window.clearTimeout(timer);
        if (serial === this.requestSerial) { this.setLoadingState(false); this.pendingQuery = null; this.pendingRequest = null; }
      }
    })();
    return this.pendingRequest;
  }

  renderSearchResults(incoming, original) {
    if (!incoming || !this.resultsElement) { this.showError(); return; }
    this.resultsElement.innerHTML = incoming.innerHTML;
    this.resultsElement.dataset.predictiveProductCount = incoming.dataset.predictiveProductCount || '0';
    this.resultsElement.dataset.predictiveCollectionCount = incoming.dataset.predictiveCollectionCount || '0';
    this.resultsElement.dataset.predictiveDisplayTerm = original;
    for (const copy of this.resultsElement.querySelectorAll('[data-predictive-display-copy]')) {
      copy.textContent = (copy.dataset.copyTemplate || '').split('__QUERY__').join(original);
    }
    const resolved = this.resolvedQuery?.term || original;
    const correction = this.resultsElement.querySelector('[data-search-correction]');
    if (correction && resolved.toLocaleLowerCase() !== original.toLocaleLowerCase()) {
      correction.textContent = (this.dataset.correctionTemplate || '').split('__QUERY__').join(resolved);
      correction.hidden = false;
    }
    for (const link of this.resultsElement.querySelectorAll('[data-search-view-all]')) {
      const url = new URL(this.form.action, window.location.origin);
      url.search = new URLSearchParams({ q: resolved, type: 'product', 'options[prefix]': 'last' });
      link.href = url;
    }
    this.searchContent.classList.remove('hidden');
    this.setAttribute('results', 'true');
    this.clearError();
    this.input.setAttribute('aria-expanded', 'true');
    const empty = Boolean(this.resultsElement.querySelector('[data-predictive-results-empty]'));
    this.announce(this.formatAnnouncement(empty ? this.announcements.resultsEmpty : this.announcements.resultsAvailable));
  }

  showError() {
    this.removeAttribute('results');
    this.setAttribute('error', 'true');
    this.errorElement?.classList.remove('hidden');
    this.searchContent?.classList.remove('hidden');
    this.input.setAttribute('aria-expanded', 'true');
    this.announce(this.announcements.unavailable);
  }

  clearError() { this.removeAttribute('error'); this.errorElement?.classList.add('hidden'); }

  updateResetState() {
    if (this.resetButton) {
      const empty = !this.input.value;
      this.resetButton.disabled = empty;
      this.resetButton.hidden = empty;
      this.resetButton.tabIndex = empty ? -1 : 0;
    }
    this.syncAnimatedPlaceholder();
  }

  initializeAnimatedPlaceholder() {
    if (!this.animatedPlaceholderWord || !this.placeholderWords.length) return;

    this.placeholderIndex = Math.max(0, this.placeholderWords.indexOf(this.animatedPlaceholderWord.textContent.trim()));
    this.placeholderColorIndex = this.placeholderIndex % Math.max(1, this.placeholderColors.length);
    this.placeholderMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.placeholderReady = true;
    this.placeholderVisible = !('IntersectionObserver' in window);
    const refresh = () => this.updatePlaceholderAnimation();
    const { signal } = this.abortController;
    this.placeholderMotion.addEventListener('change', refresh, { signal });
    document.addEventListener('visibilitychange', refresh, { signal });
    if ('IntersectionObserver' in window) {
      this.placeholderObserver = new IntersectionObserver(entries => {
        this.placeholderVisible = entries.some(entry => entry.isIntersecting && entry.intersectionRatio > 0);
        refresh();
      });
      this.placeholderObserver.observe(this.input);
    }
    this.applyAnimatedPlaceholderColor();
    this.syncAnimatedPlaceholder();
  }

  placeholderCanAnimate() {
    return this.placeholderReady && this.isConnected && this.placeholderVisible && !document.hidden
      && !this.placeholderMotion.matches && document.activeElement !== this.input && !this.getQuery()
      && (!this.input.checkVisibility || this.input.checkVisibility({ visibilityProperty: true, contentVisibilityAuto: true }));
  }

  updatePlaceholderAnimation() {
    if (!this.placeholderReady) return;
    if (this.placeholderCanAnimate()) {
      if (!this.placeholderTimer && !this.placeholderSwapTimer) this.scheduleAnimatedPlaceholderHide();
    } else {
      window.clearTimeout(this.placeholderTimer);
      window.clearTimeout(this.placeholderSwapTimer);
      this.placeholderTimer = this.placeholderSwapTimer = null;
      this.animatedPlaceholderWord.classList.remove('is-revealing', 'is-hiding');
    }
  }

  scheduleAnimatedPlaceholderHide(delay = 3500) {
    window.clearTimeout(this.placeholderTimer);
    this.placeholderTimer = window.setTimeout(() => {
      this.placeholderTimer = null;
      if (!this.placeholderCanAnimate()) { this.updatePlaceholderAnimation(); return; }

      this.hideAnimatedPlaceholderWord();
      window.clearTimeout(this.placeholderSwapTimer);
      this.placeholderSwapTimer = window.setTimeout(() => {
        this.placeholderSwapTimer = null;
        if (!this.placeholderCanAnimate()) { this.updatePlaceholderAnimation(); return; }
        this.placeholderIndex = (this.placeholderIndex + 1) % this.placeholderWords.length;
        this.placeholderColorIndex = (this.placeholderColorIndex + 1) % Math.max(1, this.placeholderColors.length);
        this.animatedPlaceholderWord.textContent = this.placeholderWords[this.placeholderIndex];
        this.applyAnimatedPlaceholderColor();
        this.revealAnimatedPlaceholderWord();
        this.scheduleAnimatedPlaceholderHide();
      }, 500);
    }, delay);
  }

  applyAnimatedPlaceholderColor() {
    if (!this.animatedPlaceholderWord || !this.placeholderColors.length) return;
    this.animatedPlaceholderWord.style.setProperty(
      '--tm-search-gradient-color',
      this.placeholderColors[this.placeholderColorIndex]
    );
  }

  revealAnimatedPlaceholderWord() {
    if (!this.animatedPlaceholderWord) return;
    this.animatedPlaceholderWord.classList.remove('is-hiding');
    this.animatedPlaceholderWord.classList.add('is-revealing');
  }

  hideAnimatedPlaceholderWord() {
    if (!this.animatedPlaceholderWord) return;
    this.animatedPlaceholderWord.classList.remove('is-revealing');
    this.animatedPlaceholderWord.classList.add('is-hiding');
  }

  syncAnimatedPlaceholder() {
    if (!this.animatedPlaceholder) return;
    const shouldHide = Boolean(this.getQuery()) || document.activeElement === this.input;
    this.animatedPlaceholder.toggleAttribute('hidden', shouldHide);
    this.updatePlaceholderAnimation();
  }

  setLoadingState(loading) {
    this.toggleAttribute('loading', loading);
    this.resetButton?.classList.toggle(this.states.LOADING, loading);
    this.resultsElement?.setAttribute('aria-busy', loading ? 'true' : 'false');
    if (this.loadingElement) this.loadingElement.hidden = !loading;
    if (loading) this.announce(this.announcements.loading);
  }

  formatAnnouncement(template) { return String(template || '').split('__QUERY__').join(this.getQuery()); }
  announce(message) { if (this.statusElement) this.statusElement.textContent = message; }

  setViewportMetrics() {
    const viewport = window.visualViewport;
    const viewportBottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
    const anchor = this.isHeaderPanelSearch && !this.panel.hidden ? this.panel : this.header || this.form;
    const bottom = Math.round(anchor.getBoundingClientRect().bottom);
    this.style.setProperty('--search-panel-top', `${bottom}px`);
    this.style.setProperty('--search-panel-height', `${Math.max(0, Math.floor(viewportBottom - bottom - (this.header ? 0 : 12)))}px`);
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    if (this.header) document.documentElement.style.setProperty('--header-bottom-position', `${bottom}px`);
  }

  handleViewportResize() {
    if (!this.isOpen) return;
    if (this.getClientRects().length && (!this.isHeaderPanelSearch || this.panel.getClientRects().length)) {
      this.setViewportMetrics();
      return;
    }
    const query = this.getQuery();
    this.dismiss();
    if (!this.header) return;
    // The header has separate desktop and mobile fields; keep the current task
    // when a resize switches between them instead of discarding the open search.
    const replacement = Array.from(this.header.querySelectorAll('predictive-search')).find(search =>
      search !== this && search.isConnectedToSearch &&
      (search.getClientRects().length || search.triggers.some(trigger => trigger.getClientRects().length))
    );
    if (!replacement) return;
    replacement.input.value = query;
    replacement.resolvedQuery = null;
    replacement.cachedMap = new Map(this.cachedMap);
    replacement.updateResetState();
    if (replacement.isHeaderPanelSearch) {
      replacement.open(replacement.triggers.find(trigger => trigger.getClientRects().length));
    } else {
      replacement.showInitialContent();
      replacement.input.focus({ preventScroll: true });
    }
  }

  toggleSearchState(open) {
    this.classList.toggle(this.states.OPEN, open);
    if (this.isHeaderSearch) {
      if (open) document.body.classList.add(this.states.SEARCH_OPEN);
      else if (!document.querySelector(`header predictive-search.${this.states.OPEN}`)) document.body.classList.remove(this.states.SEARCH_OPEN);
    }
    if (!open) {
      this.submitSerial = (this.submitSerial || 0) + 1;
      this.cancelRequest();
      this.input?.setAttribute('aria-expanded', 'false');
      this.searchContent?.classList.add('hidden');
    }
  }
}

defineCustomElement('predictive-search', PredictiveSearch);
})();
